// Determines which suggestions a reviewer has left undecided, so a completing
// action can interrupt and ask about them.
//
// The per-field rules mirror useAutoDocExtSuggestion's `showSuggestion` gate:
// a suggestion is outstanding when it exists, its field is empty, its field is
// enabled, and no decision has been recorded. What differs is the scope — the
// hook answers for one mounted field, this answers for every suggestible field
// on the surface, including fields the reviewer never scrolled to.

import {
  readSuggestionStatusFromBucket,
  SuggestionStatusBucket,
} from './suggestionStatusStorage';
import { AutoDocumentExtraction } from 'query/study';

export type SuggestibleField = {
  // Canonical field name the suggestion pipeline uses, and the key the
  // aggregate endpoint returns suggestions under.
  autoDocExtField: string;
  // Key this field's decision is recorded under in the status bucket. DE1
  // keys by form path, DE2 by block id — hence separate from autoDocExtField.
  statusKey: string;
  // Field name as shown to the reviewer.
  label: string;
};

export type OutstandingSuggestion = SuggestibleField & {
  suggestion: string;
};

type SetStatusBucket = (
  update:
    | SuggestionStatusBucket
    | ((prev: SuggestionStatusBucket) => SuggestionStatusBucket)
) => void;

type FindOutstandingSuggestionsArgs<Field extends SuggestibleField> = {
  fields: Field[];
  suggestionsByField: Record<string, AutoDocumentExtraction>;
  statusBucket: SuggestionStatusBucket;
  isFieldEmpty: (field: Field) => boolean;
  isFieldEnabled: (field: Field, suggestion: string) => boolean;
};

// Only null / undefined count as empty, matching useAutoDocExtSuggestion: an
// empty string means the reviewer has already been in the field.
export const isFormValueEmpty = (value: unknown): boolean =>
  value === null || value === undefined;

const suggestionTextFor = (
  extraction: AutoDocumentExtraction | undefined
): string => extraction?.suggestion ?? '';

export const findOutstandingSuggestions = <Field extends SuggestibleField>({
  fields,
  suggestionsByField,
  statusBucket,
  isFieldEmpty,
  isFieldEnabled,
}: FindOutstandingSuggestionsArgs<Field>): (Field & {
  suggestion: string;
})[] =>
  fields.flatMap((field) => {
    const suggestion = suggestionTextFor(
      suggestionsByField[field.autoDocExtField]
    );
    if (!suggestion) return [];

    const isDecided =
      readSuggestionStatusFromBucket(statusBucket, field.statusKey) !==
      'pending';
    if (isDecided) return [];

    if (!isFieldEnabled(field, suggestion) || !isFieldEmpty(field)) return [];

    return [{ ...field, suggestion }];
  });

export const rejectAllSuggestions = (
  setStatusBucket: SetStatusBucket,
  outstanding: OutstandingSuggestion[]
): void => {
  if (outstanding.length === 0) return;

  const rejections = Object.fromEntries(
    outstanding.map(({ statusKey }) => [statusKey, 'rejected' as const])
  );

  setStatusBucket((previous) => ({ ...previous, ...rejections }));
};
